import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import {
  Rocket,
  Megaphone,
  Sparkles,
  Globe2,
  Swords,
  Workflow,
  LayoutDashboard,
  ArrowRight,
  X
} from 'lucide-react';

interface WelcomeModalProps {
  open: boolean;
  onStartTour: () => void;
  onSkip: () => void;
}

// Mesmos nomes e ordem do menu lateral (components/layout/navigation.ts)
const features = [
  { icon: Sparkles, colorFrom: 'from-pink-500', colorTo: 'to-pink-600', bgLight: 'bg-pink-50', bgDark: 'dark:bg-pink-950/30', textColor: 'text-pink-600 dark:text-pink-400', key: 'socialContent' },
  { icon: Globe2, colorFrom: 'from-purple-500', colorTo: 'to-purple-600', bgLight: 'bg-purple-50', bgDark: 'dark:bg-purple-950/30', textColor: 'text-purple-600 dark:text-purple-400', key: 'landingPages' },
  { icon: Megaphone, colorFrom: 'from-blue-500', colorTo: 'to-blue-600', bgLight: 'bg-blue-50', bgDark: 'dark:bg-blue-950/30', textColor: 'text-blue-600 dark:text-blue-400', key: 'campaigns' },
  { icon: Workflow, colorFrom: 'from-emerald-500', colorTo: 'to-emerald-600', bgLight: 'bg-emerald-50', bgDark: 'dark:bg-emerald-950/30', textColor: 'text-emerald-600 dark:text-emerald-400', key: 'automation' },
  { icon: Swords, colorFrom: 'from-orange-500', colorTo: 'to-orange-600', bgLight: 'bg-orange-50', bgDark: 'dark:bg-orange-950/30', textColor: 'text-orange-600 dark:text-orange-400', key: 'competitorAnalysis' },
  { icon: LayoutDashboard, colorFrom: 'from-cyan-500', colorTo: 'to-cyan-600', bgLight: 'bg-cyan-50', bgDark: 'dark:bg-cyan-950/30', textColor: 'text-cyan-600 dark:text-cyan-400', key: 'aiReports' },
];

const WelcomeModal: React.FC<WelcomeModalProps> = ({ open, onStartTour, onSkip }) => {
  const { t } = useTranslation();

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onSkip}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-2xl transition-all">
                {/* Close button */}
                <button
                  onClick={onSkip}
                  aria-label={t('common.close', 'Fechar')}
                  className="absolute top-4 right-4 z-10 p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Header with gradient */}
                <div className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-purple-600 to-indigo-700 px-5 sm:px-8 py-7 sm:py-10 text-center">
                  {/* Shimmer overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />

                  {/* Glow behind icon */}
                  <div className="relative inline-flex items-center justify-center mb-4">
                    <div className="absolute inset-0 bg-white/20 rounded-2xl blur-xl animate-pulse" />
                    <div className="relative w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
                      <Rocket className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  <Dialog.Title className="text-xl sm:text-2xl font-bold text-white mb-2">
                    {t('welcome.title', 'Welcome to DevSkin Marketing!')}
                  </Dialog.Title>
                  <p className="text-white/80 text-sm max-w-md mx-auto">
                    {t('welcome.subtitle', 'Discover everything you can do with your all-in-one marketing platform. Let us show you around!')}
                  </p>
                </div>

                {/* Feature Cards Grid */}
                <div className="px-4 sm:px-8 py-5 sm:py-6">
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4">
                    {features.map((feature) => {
                      const Icon = feature.icon;
                      return (
                        <div
                          key={feature.key}
                          className={`group p-3 sm:p-4 rounded-xl ${feature.bgLight} ${feature.bgDark} border border-gray-100 dark:border-gray-800 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5`}
                        >
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${feature.colorFrom} ${feature.colorTo} flex items-center justify-center mb-3 shadow-sm`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <h3 className={`font-semibold text-sm mb-1 ${feature.textColor}`}>
                            {t(`welcome.features.${feature.key}.title`)}
                          </h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                            {t(`welcome.features.${feature.key}.description`)}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="px-4 sm:px-8 pb-6 sm:pb-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    onClick={onStartTour}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 transition-all duration-200"
                  >
                    {t('welcome.startTour', 'Start Guided Tour')}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onSkip}
                    className="w-full sm:w-auto px-6 py-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    {t('welcome.skipForNow', 'Skip for now')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WelcomeModal;
